import { useEffect, useRef, useState } from "preact/hooks";

import { PasswordToggleInput } from "../components/PasswordToggleInput";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { ApiError, postCredentialsSetup, postPasswordReset } from "../lib/api";
import {
  GENERIC_FALLBACK_ERROR,
  MUST_CHANGE_BODY,
  MUST_CHANGE_NOT_YOU_LINE,
  MUST_CHANGE_RETURN_BODY,
  MUST_CHANGE_SUCCESS,
  ONBOARDING_NOT_YOU_LINE,
  ONBOARDING_PRIMARY_BUTTON,
  ONBOARDING_SAVE_FAILURE_ERROR,
  PASSWORD_FIELD_LABEL_CHOOSE,
  PASSWORD_TOO_LONG_ERROR,
  PASSWORD_TOO_SHORT_ERROR,
  RESET_REQUIRED_REFUSED_MESSAGE,
  USERNAME_FIELD_LABEL,
  USERNAME_HELPER_SIGN_IN_AS_PREFIX,
  USERNAME_HELPER_SIGN_IN_AS_SUFFIX,
  USERNAME_HELPER_TEXT,
  USERNAME_INVALID_ERROR,
  USERNAME_TAKEN_ERROR,
  onboardingHeading,
  onboardingSubheading,
  passwordCounterText,
  passwordMeetsMinLength,
  suggestUsername,
  usernamePreview,
  type CredentialMode,
} from "../lib/authCopy";

/** The server's `MAX_PASSWORD_LENGTH` (`app/security/passwords.py`). Only
 * used to pick which governed length error a 422 means — the server's own
 * `_PASSWORD_LENGTH_DETAIL` covers both ends with one fixed string, so the
 * client has to disambiguate from what it submitted. */
const PASSWORD_MAX_LENGTH = 256;

/** Long enough for `MUST_CHANGE_SUCCESS` to be announced by a screen reader
 * before this screen is replaced, short enough that a sighted person does
 * not experience it as a stall. */
const SUCCESS_ANNOUNCE_MS = 1200;

export interface CredentialSetupViewProps {
  mode: CredentialMode;
  /** `"must-change"` only. True when the person is coming BACK to this
   * screen having abandoned it earlier, which swaps `MUST_CHANGE_BODY`
   * ("the password you just used") for `MUST_CHANGE_RETURN_BODY` ("you're
   * still using…") per UX §6.1 B3. */
  returning?: boolean;
  /** Forced-change modes (`"must-change"` and `"reset"`). True once a
   * reset-scope 403 has been observed — something the person tapped, or a
   * queued/background request, was refused because the flag is still set.
   * Renders `RESET_REQUIRED_REFUSED_MESSAGE` as a POLITE STATUS, never an
   * alert.
   *
   * NOT gated on `"must-change"`: UX §6.1 B2 scopes this state to the FLAG
   * ("every other endpoint now answers 409 while the flag is set"), not to
   * how the flag got set. It was incidentally must-change-only before,
   * because the old client-side discriminator folded `refused` into the
   * mode choice; once §11.3's server boolean owns that choice, a person who
   * reached this screen via a link consume can be refused too — e.g. an
   * admin reissues their link while they are mid-tap. Losing the notice for
   * them would be a silent regression of the row. */
  refused?: boolean;
  /** The person's display name (`users.name`, from `/me`) — used verbatim
   * (never truncated to a first name) in the subheading/safety line, see
   * `lib/authCopy.ts::onboardingSubheading`'s docstring. */
  displayName: string;
  /** Called after a successful save — the caller re-fetches `/me`
   * (`useMe().refresh`), which will then carry `needsOnboarding: false` /
   * `passwordResetRequired: false` and route to Today. This view never
   * decides "what's next" itself, matching `LoginView`'s same contract. */
  onDone: () => void;
}

/**
 * Sprint-007 D4 (F4-3 + F4-4, ADR-002 Amendment 4 §7) — the forced first-
 * login onboarding screen AND the forced password-reset/recovery screen.
 * UX §1 is explicit that these are ONE component with two copy modes, not
 * two screens ("Same component either way — do not build two screens.") —
 * this file satisfies both ADR task IDs (F4-3 OnboardingView, F4-4
 * PasswordResetView) as a single shared implementation, which is the UX
 * doc's authored, reconciled design, not a scope-reduction.
 *
 * No confirm-password field (UX §1.1: the show/hide toggle defaults to
 * visible text, so a second blind-retype field is pure friction with no
 * accuracy benefit — a deliberate simplification, not an oversight). No
 * optional Face ID/fingerprint step after save (UX §1.3) — that is
 * sprint-007 D6 (passkeys), explicitly out of this task's scope; on
 * success this view calls `onDone()` directly, which lands the person on
 * Today exactly as if they had tapped "Not now" on that (unbuilt) screen.
 */
export function CredentialSetupView({
  mode,
  returning = false,
  refused = false,
  displayName,
  onDone,
}: CredentialSetupViewProps) {
  const [username, setUsername] = useState(() => suggestUsername(displayName));
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `"must-change"` only — see the success line in the form below.
  const [succeeded, setSucceeded] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useFocusHeadingOnMount(headingRef);

  useEffect(() => {
    return () => {
      if (doneTimerRef.current !== null) clearTimeout(doneTimerRef.current);
    };
  }, []);

  const meetsLength = passwordMeetsMinLength(password.length);
  const tooLong = password.length > PASSWORD_MAX_LENGTH;

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (submitting) return;

    // UX §1.2: the primary button is disabled below the length floor, but
    // the inline "a little longer" error is a defensive fallback reachable
    // via Enter-key form submission even while the button itself is
    // disabled — never shown proactively while typing, only on a genuine
    // submit attempt. An empty/malformed username (first-time mode only)
    // is caught defensively below by the server's 422 response — the
    // field's `required` attribute is the primary guard against submitting
    // it empty.
    if (!meetsLength || tooLong) {
      setSubmitAttempted(true);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (mode === "first-time") {
        await postCredentialsSetup({ username: username.trim(), password });
      } else {
        // UX §6.1 Ruling 3 — `"must-change"` exits through
        // `/auth/password/reset` exactly as `"reset"` does, NOT
        // `/auth/password/change`. That endpoint requires no
        // `current_password`, so the person is never asked to retype the
        // password the admin read to them ninety seconds ago: a pure fail
        // point with no security value, since the live session plus the
        // server-side flag is already the proof. It is also one of the only
        // three routes a reset-scoped principal can reach at all
        // (Amendment 12 §1.5).
        await postPasswordReset({ new_password: password });
      }
      if (mode === "must-change") {
        setSucceeded(true);
        doneTimerRef.current = setTimeout(onDone, SUCCESS_ANNOUNCE_MS);
        return;
      }
      onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422 && password.length > PASSWORD_MAX_LENGTH) {
          setError(PASSWORD_TOO_LONG_ERROR);
        } else if (err.status === 422 && err.message.includes("password")) {
          setError(PASSWORD_TOO_SHORT_ERROR);
        } else if (err.status === 422) {
          setError(USERNAME_INVALID_ERROR);
        } else if (err.status === 409 && err.message.includes("taken")) {
          setError(USERNAME_TAKEN_ERROR);
        } else if (err.status === 401 || err.status === 409) {
          // Credentials already set up / reset no longer required / the
          // onboarding session itself went stale — the routing guard that
          // put us on this screen is out of date. Re-fetch `/me` and let
          // it decide the correct next screen rather than guessing here.
          onDone();
        } else {
          setError(ONBOARDING_SAVE_FAILURE_ERROR);
        }
      } else {
        setError(GENERIC_FALLBACK_ERROR);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="logged-out-screen">
      {/* Sprint-008 A5 (design-desktop-phase-a.md §4) — see `LoginView.tsx`'s
       * identical wrapper for the full rationale/contract. */}
      <div class="auth-card">
        <span class="app-wordmark">Hayloft</span>
        <h1 ref={headingRef} tabIndex={-1} class="logged-out-heading">
          {onboardingHeading(mode)}
        </h1>
        <p class="auth-subheading">{onboardingSubheading(mode, displayName)}</p>

        {/* UX §6.1 B — `"must-change"` explains WHAT HAPPENED before it asks
         * for anything. Nothing else in this app has ever handed someone a
         * password that somebody else chose, so without this line the screen
         * reads as an accusation. */}
        {mode === "must-change" ? (
          <p class="auth-helper-text">{returning ? MUST_CHANGE_RETURN_BODY : MUST_CHANGE_BODY}</p>
        ) : null}

        {/* `ONBOARDING_NOT_YOU_LINE` says "ask your farm admin for the right
         * LINK" — there is no link in the must-change flow, so it cannot be
         * reused and §6.1 gives that mode its own line. */}
        <p class="auth-safety-line">
          {mode === "must-change" ? MUST_CHANGE_NOT_YOU_LINE : ONBOARDING_NOT_YOU_LINE}
        </p>

        {/* THE STYLING HERE IS LOAD-BEARING (UX §6.1 B2). `role="status"` +
         * `aria-live="polite"`, NEVER `role="alert"`, and `.auth-queue-hint`
         * — never `.error-hint` and never `is-destructive`. A teenager who
         * taps a normal button and is met with a red assertive error has
         * just been taught that they broke the farm app. They didn't: this
         * is a queue, not a wall. Do not restyle it.
         *
         * Deliberately not `mode === "must-change" && refused` — see the
         * `refused` prop docstring. `"first-time"` cannot produce it (the
         * caller only passes `refused` on the two forced-change branches),
         * so this needs no mode guard of its own. */}
        {refused ? (
          <p class="auth-queue-hint" role="status" aria-live="polite">
            {RESET_REQUIRED_REFUSED_MESSAGE}
          </p>
        ) : null}

        <form class="auth-form" onSubmit={(e) => void handleSubmit(e)}>
          {mode === "first-time" ? (
            <>
              <label class="admin-field-label" for="setup-username">
                {USERNAME_FIELD_LABEL}
              </label>
              <input
                id="setup-username"
                class="pick-input"
                type="text"
                autocomplete="username"
                value={username}
                required
                aria-required="true"
                aria-describedby="setup-username-helper"
                disabled={submitting}
                onInput={(e) => setUsername((e.target as HTMLInputElement).value)}
              />
              {/* UX review 2026-10 — echo the ACTUAL value they'll type
               * next time (normalized exactly as the server stores it), so
               * the sign-in name can't be mistaken for their display name. */}
              <p id="setup-username-helper" class="auth-helper-text">
                {usernamePreview(username) ? (
                  <>
                    {USERNAME_HELPER_SIGN_IN_AS_PREFIX}
                    <strong class="auth-username-preview">{usernamePreview(username)}</strong>
                    {USERNAME_HELPER_SIGN_IN_AS_SUFFIX}
                  </>
                ) : (
                  USERNAME_HELPER_TEXT
                )}
              </p>
            </>
          ) : null}

          <label class="admin-field-label" for="setup-password">
            {PASSWORD_FIELD_LABEL_CHOOSE}
          </label>
          <PasswordToggleInput
            id="setup-password"
            value={password}
            autoComplete="new-password"
            disabled={submitting}
            onInput={setPassword}
          />
          <p class={`auth-counter${meetsLength ? " is-met" : ""}`}>
            {passwordCounterText(password.length)}
          </p>

          {submitAttempted && !meetsLength ? (
            <p class="error-hint" role="alert">
              {PASSWORD_TOO_SHORT_ERROR}
            </p>
          ) : null}
          {tooLong ? (
            <p class="error-hint" role="alert">
              {PASSWORD_TOO_LONG_ERROR}
            </p>
          ) : null}
          {error ? (
            <p class="error-hint" role="alert">
              {error}
            </p>
          ) : null}

          {/* UX §6.1 B — `role="status"`, then straight to the existing Face
           * ID / fingerprint step. The short beat before `onDone()` exists
           * so a screen reader actually announces this before the screen
           * changes underneath it; unmounting in the same commit would make
           * the live region unreachable. */}
          {succeeded ? (
            <p class="auth-queue-hint" role="status" aria-live="polite">
              {MUST_CHANGE_SUCCESS}
            </p>
          ) : null}

          <button
            type="submit"
            class="primary-btn"
            disabled={!meetsLength || tooLong || submitting || succeeded}
          >
            {ONBOARDING_PRIMARY_BUTTON}
          </button>
        </form>
      </div>
    </div>
  );
}
